import { getValueByPath, createAbsoluteElement, removeElement } from './helpers.js';
import { f as VueComponentMixin, B as BaseComponentMixin, c as config } from './plugins-4daf8ef8.js';
import { openBlock, createBlock, createVNode, Transition, withCtx, withDirectives, Fragment, createTextVNode, toDisplayString, renderSlot, createCommentVNode, vShow } from 'vue';

/**
 * Display a brief helper text to your user
 * @displayName Tooltip
 * @example ./examples/Tooltip.md
 * @style _tooltip.scss
 */

var script = {
  name: 'OTooltip',
  mixins: [VueComponentMixin(), BaseComponentMixin],
  props: {
    /** Whether tooltip is active or not, use the .sync modifier (Vue 2.x) or v-model:active (Vue 3.x) to make it two-way binding */
    active: {
      type: Boolean,
      default: true
    },

    /** Tooltip text */
    label: String,

    /** Tooltip delay before it appears (number in ms) */
    delay: Number,

    /**
     * Tooltip position in relation to the element
     * @values top, bottom, left, right
     */
    position: {
      type: String,
      default: () => {
        return getValueByPath(config, 'tooltip.position', 'top');
      },

      validator(value) {
        return ['top', 'bottom', 'left', 'right'].indexOf(value) > -1;
      }

    },

    /**
     * Tooltip trigger events
     * @values hover, click, focus, contextmenu
     */
    triggers: {
      type: Array,
      default: () => {
        return getValueByPath(config, 'tooltip.triggers', ['hover']);
      }
    },

    /** Tooltip will be always active */
    always: Boolean,

    /** Tooltip will have an animation */
    animated: {
      type: Boolean,
      default: true
    },

    /** Tooltip default animation */
    animation: {
      type: String,
      default: () => {
        return getValueByPath(config, 'tooltip.animation', 'fade');
      }
    },

    /**
     * Tooltip auto close options
     * @values true, false, 'inside', 'outside'
     */
    autoClose: {
      type: [Array, Boolean],
      default: true
    },

    /** Tooltip will be multilined */
    multiline: Boolean,

    /** Append tooltip content to body */
    appendToBody: Boolean,

    /**
    * Color of the tooltip
    * @values primary, info, success, warning, danger, and any other custom color
    */
    variant: String,
    rootClass: String,
    contentClass: String,
    triggerClass: String,
    multilineClass: String,
    alwaysClass: String,
    variantClass: String
  },

  data() {
    return {
      isActive: false,
      bodyEl: undefined // Used to append to body

    };
  },

  computed: {
    rootClasses() {
      return [this.computedClass('tooltip', 'rootClass', 'o-tooltip'), {
        [`${this.computedClass('tooltip', 'variantClass', 'o-color-')}${this.variant}`]: this.variant
      }, {
        [`${this.computedClass('tooltip', 'orderClass', 'o-tooltip-')}${this.position}`]: this.position
      }, {
        [this.computedClass('tooltip', 'multilineClass', 'o-tooltip-multiline')]: this.multiline
      }, {
        [this.computedClass('tooltip', 'alwaysClass', 'o-tooltip-always')]: this.always
      }];
    },

    triggerClasses() {
      return [this.computedClass('tooltip', 'triggerClass', 'o-tooltip-trigger')];
    },

    contentClasses() {
      return [this.computedClass('tooltip', 'contentClass', 'o-tooltip-content')];
    },

    newAnimation() {
      return this.animated ? this.animation : undefined;
    },

    hasDefaultSlot() {
      return this.existsSlot('default');
    }

  },
  watch: {
    isActive(value) {
      if (value && this.appendToBody) {
        this.updateAppendToBody();
      }
    }

  },
  methods: {
    updateAppendToBody() {
      const tooltip = this.$refs.tooltip;
      const trigger = this.$refs.trigger;

      if (tooltip && trigger) {
        // update wrapper tooltip
        const tooltipEl = this.$data.bodyEl.children[0];
        tooltipEl.classList.forEach(item => tooltipEl.classList.remove(item));

        if (this.$vnode && this.$vnode.data && this.$vnode.data.staticClass) {
          tooltipEl.classList.add(this.$vnode.data.staticClass);
        }

        this.rootClasses.forEach(item => {
          if (typeof item === 'object') {
            Object.keys(item).filter(key => !!item[key]).forEach(key => tooltipEl.classList.add(key));
          } else {
            tooltipEl.classList.add(item);
          }
        });
        tooltipEl.style.width = `${trigger.clientWidth}px`;
        tooltipEl.style.height = `${trigger.clientHeight}px`;
        const rect = trigger.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const left = rect.left + window.scrollX;
        const wrapper = this.$data.bodyEl;
        wrapper.style.position = 'absolute';
        wrapper.style.top = `${top}px`;
        wrapper.style.left = `${left}px`;
        wrapper.style.zIndex = this.isActive || this.always ? '9999' : '-1';
      }
    },

    onClick() {
      if (this.triggers.indexOf('click') < 0) return; // if not active, toggle after clickOutside event
      // this fixes toggling programmatic

      this.$nextTick(() => {
        setTimeout(() => this.open());
      });
    },

    onHover() {
      if (this.triggers.indexOf('hover') < 0) return;
      this.open();
    },

    onFocus() {
      if (this.triggers.indexOf('focus') < 0) return;
      this.open();
    },

    onContextMenu(event) {
      if (this.triggers.indexOf('contextmenu') < 0) return;
      event.preventDefault();
      this.open();
    },

    open() {
      if (this.delay) {
        this.timer = setTimeout(() => {
          this.isActive = true;
          this.timer = null;
        }, this.delay);
      } else {
        this.isActive = true;
      }
    },

    close() {
      if (typeof this.autoClose === 'boolean') {
        this.isActive = !this.autoClose;
      }

      if (this.autoClose && this.timer) clearTimeout(this.timer);
    },

    /**
    * Close tooltip if clicked outside.
    */
    clickedOutside(event) {
      if (this.isActive) {
        if (Array.isArray(this.autoClose)) {
          if (this.autoClose.indexOf('outside') >= 0) {
            if (!this.isInWhiteList(event.target)) this.isActive = false;
          } else if (this.autoClose.indexOf('inside') >= 0) {
            if (this.isInWhiteList(event.target)) this.isActive = false;
          }
        }
      }
    },

    /**
     * Keypress event that is bound to the document
     */
    keyPress({
      key
    }) {
      if (this.isActive && (key === 'Escape' || key === 'Esc')) {
        if (Array.isArray(this.autoClose)) {
          if (this.autoClose.indexOf('escape') >= 0) this.isActive = false;
        }
      }
    },

    /**
    * White-listed items to not close when clicked.
    */
    isInWhiteList(el) {
      if (el === this.$refs.content) return true; // All chidren from content

      if (this.$refs.content !== undefined) {
        const children = this.$refs.content.querySelectorAll('*');

        for (const child of children) {
          if (el === child) {
            return true;
          }
        }
      }

      return false;
    }

  },

  mounted() {
    if (this.appendToBody) {
      this.$data.bodyEl = createAbsoluteElement(this.$refs.content);
      this.updateAppendToBody();
    }
  },

  created() {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.clickedOutside);
      document.addEventListener('keyup', this.keyPress);
    }
  },

  beforeDestroy() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', this.clickedOutside);
      document.removeEventListener('keyup', this.keyPress);
    }

    if (this.appendToBody) {
      removeElement(this.$data.bodyEl);
    }
  }

};

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return (_ctx.vueReady)
    ? (openBlock(), createBlock("span", {
        key: 0,
        ref: "tooltip",
        class: $options.rootClasses
      }, [
        createVNode(Transition, { name: $options.newAnimation }, {
          default: withCtx(() => [
            withDirectives(createVNode("div", {
              ref: "content",
              class: $options.contentClasses
            }, [
              ($props.label)
                ? (openBlock(), createBlock(Fragment, { key: 0 }, [
                    createTextVNode(toDisplayString($props.label), 1 /* TEXT */)
                  ], 64 /* STABLE_FRAGMENT */))
                : ($options.hasDefaultSlot)
                  ? renderSlot(_ctx.$slots, "content", { key: 1 })
                  : createCommentVNode("v-if", true)
            ], 2 /* CLASS */), [
              [vShow, $props.active && ($data.isActive || $props.always)]
            ])
          ]),
          _: 1
        }, 8 /* PROPS */, ["name"]),
        createVNode("div", {
          ref: "trigger",
          class: $options.triggerClasses,
          onClick: _cache[1] || (_cache[1] = (...args) => ($options.onClick(...args))),
          onContextmenu: _cache[2] || (_cache[2] = (...args) => ($options.onContextMenu(...args))),
          onMouseenter: _cache[3] || (_cache[3] = (...args) => ($options.onHover(...args))),
          onFocusCapture: _cache[4] || (_cache[4] = (...args) => ($options.onFocus(...args))),
          onMouseleave: _cache[5] || (_cache[5] = (...args) => ($options.close(...args)))
        }, [
          renderSlot(_ctx.$slots, "default", { ref: "slot" })
        ], 34 /* CLASS, HYDRATE_EVENTS */)
      ], 2 /* CLASS */))
    : createCommentVNode("v-if", true)
}

script.render = render;
script.__file = "oruga/src/components/tooltip/Tooltip.vue";

export { script as s };
